import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLock, FaStar, FaArrowRight, FaFutbol } from 'react-icons/fa';
import Navbar from '../components/Layout/Navbar';
import Button from '../components/UI/Button';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

function Themes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPremium = user?.plan === 'premium' || user?.role === 'admin';

  const [themes, setThemes]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTheme, setSelectedTheme]       = useState(null);

  useEffect(() => {
    api.get('/themes')
      .then(({ data }) => setThemes(data.themes || []))
      .catch(() => setThemes([]))
      .finally(() => setLoading(false));
  }, []);

  const handleThemeClick = (theme) => {
    if (!isPremium) {
      setSelectedTheme(theme);
      setShowUpgradeModal(true);
      return;
    }
    navigate(`/themes/${theme.id}`);
  };

  return (
    <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '90px' }}>
      <Navbar showBack title="THÈMES PREMIUM" />

      {/* Header */}
      <div className="px-5 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <FaStar className="text-qfl-gold" size={16} />
          <span
            className="text-qfl-gold font-black uppercase tracking-widest text-sm"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            Contenu Exclusif
          </span>
        </div>
        <p className="text-white/50 text-xs leading-relaxed">
          Des questionnaires spécialisés sur tes compétitions préférées,
          réservés aux membres <strong className="text-qfl-gold">Premium</strong>.
        </p>

        {!isPremium && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 rounded-2xl flex items-center gap-3"
            style={{
              background: 'linear-gradient(135deg, rgba(240,180,41,0.12), rgba(240,180,41,0.06))',
              border: '1px solid rgba(240,180,41,0.3)',
            }}
          >
            <FaLock className="text-qfl-gold flex-shrink-0" size={18} />
            <div className="flex-1 min-w-0">
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Passe Premium pour jouer
              </p>
              <p className="text-white/50 text-xs">Accès illimité à tous les thèmes</p>
            </div>
            <button
              onClick={() => navigate('/shop')}
              className="text-qfl-gold font-black text-xs uppercase tracking-wider whitespace-nowrap"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Voir →
            </button>
          </motion.div>
        )}
      </div>

      {/* Grille des thèmes */}
      <div className="flex-1 px-5 pt-2">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
          </div>
        ) : themes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <FaFutbol size={40} className="text-white/20" />
            <p className="text-white/40 text-sm text-center">
              Aucun thème disponible pour l'instant.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {themes.map((theme, i) => (
              <ThemeCard
                key={theme.id}
                theme={theme}
                isPremium={isPremium}
                index={i}
                onClick={() => handleThemeClick(theme)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modal upgrade */}
      <AnimatePresence>
        {showUpgradeModal && selectedTheme && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              className="w-full max-w-sm rounded-3xl p-6 flex flex-col gap-4"
              style={{
                background: 'linear-gradient(160deg, #1a3020, #0d2b1a)',
                border: '1px solid rgba(240,180,41,0.4)',
              }}
              initial={{ scale: 0.88, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.88, y: 24 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Icône thème */}
              <div className="flex flex-col items-center gap-2 pt-2">
                <div
                  className="w-16 h-16 rounded-2xl overflow-hidden flex items-center justify-center"
                  style={{ background: selectedTheme.bg_color || '#1a4a2e' }}
                >
                  {selectedTheme.image ? (
                    <img
                      src={`/themes/${selectedTheme.image}`}
                      alt={selectedTheme.label}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-4xl">{selectedTheme.icon}</span>
                  )}
                </div>
                <h3
                  className="text-white font-black text-xl uppercase tracking-wider text-center"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}
                >
                  {selectedTheme.label}
                </h3>
                <span
                  className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                  style={{
                    background: 'rgba(240,180,41,0.15)',
                    border: '1px solid rgba(240,180,41,0.4)',
                    color: '#f0b429',
                    fontFamily: "'Rajdhani', sans-serif",
                  }}
                >
                  ⭐ Contenu Premium
                </span>
              </div>

              <p className="text-white/60 text-sm text-center leading-relaxed">
                Ce thème est réservé aux membres <strong className="text-qfl-gold">Premium</strong>.
                Passe Premium pour accéder à tous les thèmes exclusifs et booster ta progression !
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  variant="gold"
                  size="md"
                  fullWidth
                  onClick={() => { setShowUpgradeModal(false); navigate('/shop'); }}
                >
                  <FaStar size={14} />
                  Passer Premium
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  onClick={() => setShowUpgradeModal(false)}
                >
                  Fermer
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ThemeCard({ theme, isPremium, index, onClick }) {
  const locked = !isPremium;

  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06 }}
      whileTap={!locked ? { scale: 0.97 } : {}}
      onClick={onClick}
      className="w-full text-left rounded-2xl overflow-hidden relative"
      style={{
        background: theme.bg_color || '#1a4a2e',
        border: `1.5px solid ${locked ? 'rgba(255,255,255,0.1)' : (theme.color || '#f0b429') + '44'}`,
        opacity: locked ? 0.85 : 1,
      }}
    >
      <div className="flex items-center gap-4 p-4">
        {/* Icône / Image */}
        <div
          className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{
            background: locked ? 'rgba(255,255,255,0.06)' : (theme.bg_color || '#1a4a2e'),
            border: `1px solid ${locked ? 'rgba(255,255,255,0.1)' : (theme.color || '#f0b429') + '40'}`,
          }}
        >
          {locked ? (
            <span className="text-3xl">🔒</span>
          ) : theme.image ? (
            <img
              src={`/themes/${theme.image}`}
              alt={theme.label}
              className="w-full h-full object-cover"
              style={{ opacity: 0.9 }}
            />
          ) : (
            <span className="text-3xl">{theme.icon}</span>
          )}
        </div>

        {/* Infos */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span
              className="text-white font-black text-base uppercase tracking-wider truncate"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {theme.label}
            </span>
            {locked && (
              <span
                className="px-1.5 py-0.5 rounded-md text-xs font-bold uppercase"
                style={{
                  background: 'rgba(240,180,41,0.15)',
                  border: '1px solid rgba(240,180,41,0.3)',
                  color: '#f0b429',
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: '0.6rem',
                  letterSpacing: '0.08em',
                  flexShrink: 0,
                }}
              >
                Premium
              </span>
            )}
          </div>
          {theme.description && (
            <p className="text-white/40 text-xs truncate">{theme.description}</p>
          )}
          <p
            className="text-xs font-bold mt-1"
            style={{
              color: locked ? 'rgba(255,255,255,0.2)' : (theme.color || '#f0b429'),
              fontFamily: "'Rajdhani', sans-serif",
            }}
          >
            {theme.question_count > 0
              ? `${theme.question_count} question${theme.question_count > 1 ? 's' : ''}`
              : 'Questions à venir'}
          </p>
        </div>

        {/* Flèche */}
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{
            background: locked ? 'rgba(255,255,255,0.05)' : `${theme.color || '#f0b429'}22`,
            border: `1px solid ${locked ? 'rgba(255,255,255,0.1)' : (theme.color || '#f0b429') + '55'}`,
          }}
        >
          {locked
            ? <FaLock size={13} className="text-white/30" />
            : <FaArrowRight size={13} style={{ color: theme.color || '#f0b429' }} />
          }
        </div>
      </div>

      {/* Ligne de couleur en haut (uniquement si déverrouillé) */}
      {!locked && (
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: `linear-gradient(90deg, transparent, ${theme.color || '#f0b429'}, transparent)` }}
        />
      )}
    </motion.button>
  );
}

export default Themes;
